// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CludoSearchComponent from "@awc/App/Components/Common/CludoSearchComponent";
import XngUtilities from "@awc/App/Components/Common/XngUtilities";
import XngZone from "@awc/App/Components/Common/XngZone";
import useSize from "@awc/App/Hooks/useSize";
import { CustomAppState } from "@awc/App/Store/Reducers";
import { getCartLinkWidget, getHeaderSignInWidget, getLogoImageWidget } from "@awc/App/Store/Selectors/HeaderSelectors";
import LazyImage, { LazyImageProps } from "@awc/MobiusOverrides/LazyImage";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrencies, getLanguages, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    // XNG-Change
    cartLinkProps: getCartLinkWidget(state),
    isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
    headerSignInProps: getHeaderSignInWidget(state),
    mobileSearch: (state as CustomAppState).xHeader.mobileSearch,
    languages: getLanguages(state),
    currencies: getCurrencies(state),
    logoImageWidget: getLogoImageWidget(state),
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory &
    HasShellContext;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // XNG-Changes
    topSection?: InjectableCss;
    middleSection?: InjectableCss;
    logo?: InjectableCss;
    search?: InjectableCss;
    actions?: InjectableCss;
    mainNavigation?: InjectableCss;
    middleRightSection?: InjectableCss;
    searchAndCart?: InjectableCss;
    headerLinks?: InjectableCss;
    headerSigninWrapper?: InjectableCss;
    cartlinkWrapper?: InjectableCss;
    topSectionLanguageCurrencyWrapper?: InjectableCss;
    languageAndCurrencyWrapper?: InjectableCss;
    printLogo?: InjectableCss;
    printLogoImage?: LazyImageProps;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    // XNG-Change
    shellContext: { isInShell },
    isMobileScreen,
    mobileSearch,
    languages,
    currencies,
    logoImageWidget,
}: Props) => {
    // <XNG-Changes>
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef)?.height;

    useEffect(() => {
        if (headerHeight) {
            if (typeof document === "undefined") {
                return;
            }
            const cssRoot = document.querySelector(":root") as HTMLElement;
            cssRoot.style.setProperty("--headerHeight", `${headerHeight + 1}px`);
        }
    }, [headerHeight]);

    // <XNG-Changes>

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {/* XNG-Changes: add logo for print view */}
            {logoImageWidget && (
                <StyledWrapper {...styles.printLogo}>
                    <LazyImage
                        src={logoImageWidget.logoImage}
                        altText={translate("Home")}
                        {...styles.printLogoImage}
                        disableLazyLoad={true}
                    />
                </StyledWrapper>
            )}
            <Page
                as="header"
                isHeader={true}
                {...styles.page}
                // XNg-Cahnges
                id="main-header"
            >
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    {/* XNG-Changes: structure zones */}
                    <StyledWrapper id="header-container" ref={headerRef}>
                        <StyledWrapper {...styles.container}>
                            <StyledWrapper {...styles.topSection} {...{ isInShell }}>
                                {!isMobileScreen && (
                                    <>
                                        <XngZone hint="Top Section" contentId={id} zoneName="topSection" />
                                        <StyledWrapper {...styles.topSectionLanguageCurrencyWrapper}>
                                            <StyledWrapper
                                                {...styles.languageAndCurrencyWrapper}
                                                {...{
                                                    moreThanOneLanguage: languages && languages?.length > 1,
                                                    moreThanOneCurrency: currencies && currencies?.length > 1,
                                                }}
                                            >
                                                <XngZone
                                                    hint="Language Selector"
                                                    contentId={id}
                                                    zoneName="languageSelector"
                                                />
                                                <XngZone
                                                    hint="Currency Selector"
                                                    contentId={id}
                                                    zoneName="currencySelector"
                                                />
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </>
                                )}
                            </StyledWrapper>
                            <StyledWrapper {...styles.middleSection} className={mobileSearch ? "has-padding" : ""}>
                                <StyledWrapper {...styles.logo} data-text-selector="logo">
                                    <XngZone hint="Logo" contentId={id} zoneName="Logo" />
                                </StyledWrapper>
                                <StyledWrapper {...styles.middleRightSection}>
                                    <StyledWrapper {...styles.headerLinks}>
                                        <XngZone hint="Links" contentId={id} zoneName="Links" />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.searchAndCart}>
                                        {isMobileScreen && (
                                            <StyledWrapper {...styles.headerSigninWrapper}>
                                                {createWidgetElement("Common/HeaderSignIn", {
                                                    fields: {
                                                        visibilityState: "both",
                                                        icon: "Account",
                                                        includeAccountMenu: false,
                                                        showPricingToggle: false,
                                                        showInventoryToggle: false,
                                                    },
                                                })}
                                            </StyledWrapper>
                                        )}
                                        <StyledWrapper
                                            {...styles.search}
                                            data-text-selector="search"
                                            className={mobileSearch ? "mobile-search-opened" : ""}
                                        >
                                            <XngZone hint="Search" contentId={id} zoneName="Search" />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.actions} data-text-selector="cartLink">
                                            {!isMobileScreen && (
                                                <XngZone hint="CartLink" contentId={id} zoneName="CartLink" />
                                            )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.mainNavigation} data-text-selector="main-navigation">
                                <XngZone hint="Main Navigation" contentId={id} zoneName="MainNavigation" />
                            </StyledWrapper>
                            {isMobileScreen && (
                                <StyledWrapper {...styles.cartlinkWrapper}>
                                    {createWidgetElement("Common/CartLink", {
                                        fields: {
                                            visibilityState: "icon",
                                            icon: "cart",
                                        },
                                    })}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
            {/* XNG-Changes */}
            <XngUtilities />
            <CludoSearchComponent />
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(withIsInShell(Header)))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
